import { useGetEntriesQuery } from '@/api/squares/getEntries';
import { useGetPeriodResultsQuery } from '@/api/squares/getPeriodResults';
import { ChevronLeft, ChevronRight, Loader2, Search } from 'lucide-react';
import { TzDateDisplay } from '@/components/datetime/TzDateDisplay';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useEffect, useMemo, useState } from 'react';
import { useFormikContext } from 'formik';
import { SquareFormValues } from '@/utils/forms/squareTransformers';

const PAGE_LIMIT = 50;

interface EntriesSectionProps {
  disabled?: boolean;
  squareId: string | null;
}

/**
 * Entries Section Component
 * Displays all contest entries with square numbers, user information, and scoring results
 * Matches the production mockup design exactly
 */
export function EntriesSection({ disabled: _disabled = false, squareId }: EntriesSectionProps) {
  const { values } = useFormikContext<SquareFormValues>();

  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);

  // Debounce search input and reset to page 1 when it changes
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const queryParams = { page, limit: PAGE_LIMIT, search: debouncedSearch || undefined };

  const {
    data: entriesData,
    isLoading: entriesLoading,
    error: entriesError,
  } = useGetEntriesQuery(squareId, queryParams);

  // Fetch period results to show winning numbers and per-period winner status
  const { data: periodResultsData } = useGetPeriodResultsQuery(squareId);

  const periodResults = periodResultsData?.periodResults || [];
  const periodResultByCode = useMemo(() => {
    const map = new Map<string, (typeof periodResults)[number]>();
    periodResults.forEach(pr => map.set(pr.periodCode, pr));
    return map;
  }, [periodResults]);

  // Calculate winners and prize info per period
  const periodStats = useMemo(() => {
    const stats: Record<
      string,
      {
        winners: number;
        totalPrize: number;
        prizePerWinner: number;
        isGraded: boolean;
      }
    > = {};

    periodResults.forEach(pr => {
      const winners = pr.winners?.length || 0;
      // Get prize amount from form values or default to 0
      const periods = (values.periods || []) as Array<{ periodCode: string; prizeAmount?: number }>;
      const periodConfig = periods.find(p => p.periodCode === pr.periodCode);
      const totalPrize = periodConfig?.prizeAmount
        ? parseFloat(String(periodConfig.prizeAmount))
        : 0;
      const prizePerWinner = winners > 0 ? totalPrize / winners : 0;

      stats[pr.periodCode] = {
        winners,
        totalPrize,
        prizePerWinner,
        isGraded: true,
      };
    });

    // Also check for periods that haven't been graded yet
    const periods = (values.periods || []) as Array<{ periodCode: string; prizeAmount?: number }>;
    periods.forEach(period => {
      if (!stats[period.periodCode]) {
        const totalPrize = period.prizeAmount ? parseFloat(String(period.prizeAmount)) : 0;
        stats[period.periodCode] = {
          winners: 0,
          totalPrize,
          prizePerWinner: 0,
          isGraded: false,
        };
      }
    });

    return stats;
  }, [periodResults, values.periods]);

  // Get all periods (graded and ungraded) for summary cards
  // MUST be before any early returns to avoid hook count mismatch
  const allPeriods = useMemo(() => {
    const periods = (values.periods || []) as Array<{ periodCode: string; prizeAmount?: number }>;
    const periodMap = new Map<string, { periodCode: string; prizeAmount?: number }>();

    // Add all periods from form
    periods.forEach(p => periodMap.set(p.periodCode, p));

    // Add graded periods that might not be in form yet
    periodResults.forEach(pr => {
      if (!periodMap.has(pr.periodCode)) {
        periodMap.set(pr.periodCode, { periodCode: pr.periodCode });
      }
    });

    return Array.from(periodMap.values());
  }, [values.periods, periodResults]);

  if (!squareId) {
    return (
      <div className="space-y-4">
        <div>
          <h3 className="text-lg font-semibold">Entries</h3>
          <p className="text-sm text-muted-foreground">
            Manage contest entries and user selections
          </p>
        </div>
        <div className="flex items-center justify-center py-12 border-2 border-dashed rounded-lg">
          <p className="text-muted-foreground">Save the contest first to view entries</p>
        </div>
      </div>
    );
  }

  if (entriesLoading) {
    return (
      <div className="space-y-4">
        <div>
          <h3 className="text-lg font-semibold">Entries</h3>
          <p className="text-sm text-muted-foreground">
            Manage contest entries and user selections
          </p>
        </div>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      </div>
    );
  }

  if (entriesError) {
    return (
      <div className="space-y-4">
        <div>
          <h3 className="text-lg font-semibold">Entries</h3>
          <p className="text-sm text-muted-foreground">
            Manage contest entries and user selections
          </p>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-600 font-semibold">Error loading entries</p>
          <p className="text-red-500 text-sm mt-1">
            {entriesError instanceof Error ? entriesError.message : 'Failed to load entries'}
          </p>
        </div>
      </div>
    );
  }

  const entries = entriesData?.entries || [];
  const totalEntries = entriesData?.totalEntries || 0;
  const totalPages = entriesData?.totalPages || 1;
  const currentPage = entriesData?.page || page;

  // Calculate the range of entries being shown
  const rangeStart = totalEntries === 0 ? 0 : (currentPage - 1) * PAGE_LIMIT + 1;
  const rangeEnd = Math.min(currentPage * PAGE_LIMIT, totalEntries);

  // Get team display names
  const rowTeamName = values.row_team_display_name || 'Row Team';
  const colTeamName = values.col_team_display_name || 'Col Team';

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold">Entries</h3>
        <p className="text-sm text-muted-foreground">
          {totalEntries} {totalEntries === 1 ? 'entry' : 'entries'} submitted
        </p>
      </div>

      {/* Summary Cards - Match mockup exactly */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {/* Total Entries Card */}
        <div className="border rounded-lg p-4 bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-700">
          <div className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
            Total Entries
          </div>
          <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {totalEntries.toLocaleString()}
          </div>
        </div>

        {/* Period Cards */}
        {allPeriods.map(period => {
          const stats = periodStats[period.periodCode] || {
            winners: 0,
            totalPrize: period.prizeAmount ? parseFloat(String(period.prizeAmount)) : 0,
            prizePerWinner: 0,
            isGraded: periodResults.some(pr => pr.periodCode === period.periodCode),
          };
          const pr = periodResultByCode.get(period.periodCode);

          return (
            <div
              key={period.periodCode}
              className={`border rounded-lg p-4 ${
                stats.isGraded
                  ? 'bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-700'
                  : 'bg-gray-50 dark:bg-gray-800 border-gray-300 dark:border-gray-700'
              }`}
            >
              <div className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                {stats.winners} / ${stats.totalPrize.toFixed(2)} {period.periodCode} - Winners
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {stats.isGraded
                  ? stats.winners > 0
                    ? `$${stats.prizePerWinner.toFixed(2)} per winner`
                    : 'No winners'
                  : 'Not graded yet'}
              </div>
              {stats.isGraded && pr && (
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Winning digits: <span className="font-semibold">{pr.rowLastDigit}</span> /{' '}
                  <span className="font-semibold">{pr.colLastDigit}</span> (square #
                  {pr.winningSquareNumber})
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Search Bar - always shown when squareId is set */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
        <Input
          type="text"
          placeholder="Search by SSO ID or username..."
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          className="pl-10"
        />
      </div>

      {totalEntries === 0 && !debouncedSearch ? (
        <div className="flex items-center justify-center py-24 border-2 border-dashed rounded-lg">
          <div className="text-center">
            <h4 className="text-xl font-semibold mb-2">No entries yet</h4>
            <p className="text-muted-foreground">
              Entries will appear here once users submit their selections
            </p>
          </div>
        </div>
      ) : entries.length === 0 ? (
        <div className="flex items-center justify-center py-12 border-2 border-dashed rounded-lg">
          <p className="text-muted-foreground">No entries match your search</p>
        </div>
      ) : (
        <>
          <div className="border rounded-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase dark:text-gray-400">
                      SSO USER ID
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase dark:text-gray-400">
                      TILE
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase dark:text-gray-400">
                      {rowTeamName.toUpperCase()}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase dark:text-gray-400">
                      {colTeamName.toUpperCase()}
                    </th>
                    {allPeriods.map(period => (
                      <th
                        key={period.periodCode}
                        className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase dark:text-gray-400 whitespace-nowrap"
                        title={`Period: ${period.periodCode}`}
                      >
                        {period.periodCode}
                      </th>
                    ))}
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase dark:text-gray-400">
                      ENTRY DATE
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                  {entries.map(entry => (
                    <tr
                      key={entry.selectionId}
                      className={`hover:bg-gray-50 dark:hover:bg-gray-800 ${
                        entry.isWinner ? 'bg-green-50 dark:bg-green-900/20' : ''
                      }`}
                    >
                      <td className="px-4 py-3 text-sm font-mono text-gray-900 dark:text-gray-100">
                        {entry.ssoUserId || entry.userId}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 font-medium">
                        {entry.squareNumber !== null ? `#${entry.squareNumber}` : 'N/A'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                        {entry.rowDigit !== null ? entry.rowDigit : '—'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                        {entry.colDigit !== null ? entry.colDigit : '—'}
                      </td>
                      {allPeriods.map(period => {
                        const pr = periodResultByCode.get(period.periodCode);
                        const stats = periodStats[period.periodCode] || {
                          isGraded: !!pr,
                          prizePerWinner: 0,
                        };

                        // Source-of-truth win check: digit match (requirements)
                        const isPeriodWinner =
                          !!pr &&
                          entry.rowDigit !== null &&
                          entry.colDigit !== null &&
                          entry.rowDigit === pr.rowLastDigit &&
                          entry.colDigit === pr.colLastDigit;

                        // Get stored prize amount for this period from the entry
                        const prizeAmount = entry.prizeAmountsByPeriod?.[period.periodCode] || 0;

                        return (
                          <td
                            key={`${entry.selectionId}-${period.periodCode}`}
                            className="px-2 py-3 text-sm text-center"
                            title={
                              pr
                                ? `Winning digits: ${pr.rowLastDigit}/${pr.colLastDigit} (square #${pr.winningSquareNumber})`
                                : 'No winner yet'
                            }
                          >
                            {!stats.isGraded ? (
                              <span className="text-muted-foreground text-xs">-</span>
                            ) : isPeriodWinner && prizeAmount > 0 ? (
                              <span className="text-sm font-semibold text-green-700 dark:text-green-400">
                                ${prizeAmount.toFixed(2)}
                              </span>
                            ) : (
                              <span className="text-muted-foreground text-xs">-</span>
                            )}
                          </td>
                        );
                      })}
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                        {entry.createdAt ? (
                          <TzDateDisplay
                            utcDate={entry.createdAt}
                            formatString="MMM d, yyyy h:mm a"
                          />
                        ) : (
                          'N/A'
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination Controls */}
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              {totalEntries === 0
                ? 'No entries'
                : `Showing ${rangeStart.toLocaleString()}-${rangeEnd.toLocaleString()} of ${totalEntries.toLocaleString()} entries`}
            </p>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={currentPage <= 1}
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Previous
              </Button>
              <span className="text-sm text-muted-foreground">
                Page {currentPage.toLocaleString()} of {totalPages.toLocaleString()}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage >= totalPages}
              >
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
